import { skipToken } from "@reduxjs/toolkit/query";
import type { SignalMetadataCondition } from "@repo/app-types/entities/filterConditions";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type {
  SignalId,
  SignalMetadataFieldDistribution,
} from "@repo/app-types/entities/signal";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { type ReactNode, useContext, useMemo } from "react";
import { SubmenuTrigger } from "react-aria-components";
import { useSignalDrilldownQuery } from "../../api/entities/signal.js";
import {
  formatDistributionEntryLabel,
  formatMetadataFieldKey,
} from "../signals/signalStatsFormatters.js";
import { buildSignalFilterQueryArgs } from "./buildFilterQueryArgs.js";
import { FilterContext } from "./filterContext.js";

/**
 * Metadata fields for the in-scope signal that aren't already selected and have
 * a non-empty distribution — the set that can still be added as a condition.
 */
export function useAddableMetadataFields(
  signalId: SignalId | undefined,
  playbookId: PlaybookId | undefined,
  value: readonly SignalMetadataCondition[],
): readonly SignalMetadataFieldDistribution[] {
  const { values } = useContext(FilterContext);
  const now = useNow();

  const queryArg = useMemo(() => {
    if (!signalId || !playbookId) return skipToken;
    const { scope, filters } = buildSignalFilterQueryArgs(now, {
      playbookId,
      dateRange: values.dateRange,
      teammateGroupIds: null,
      outcome: null,
      customFields: [],
      signalId,
      signalMetadataFields: [],
      totalRecordingDuration: null,
      playbookConditions: [],
    });
    return { scope, filters };
  }, [now, signalId, playbookId, values.dateRange]);

  const { data } = useSignalDrilldownQuery(queryArg);

  return useMemo(() => {
    const availableFields = data?.current.metadataBreakdown ?? [];
    return availableFields.filter(
      (f) =>
        !value.some((c) => c.fieldId === f.fieldId) &&
        f.distribution.length > 0,
    );
  }, [data, value]);
}

export function renderAddableMetadataField(
  field: SignalMetadataFieldDistribution,
  value: readonly SignalMetadataCondition[],
  onChange: (newValue: readonly SignalMetadataCondition[]) => void,
): ReactNode {
  const fieldLabel = formatMetadataFieldKey(field.fieldKey);
  return (
    <SubmenuTrigger key={field.fieldId}>
      <SimpleMenuItem id={field.fieldId}>{fieldLabel}</SimpleMenuItem>

      <SimpleMenuPopover>
        <SimpleMenu aria-label={`Filter by ${fieldLabel}`}>
          {field.distribution.map((d) => {
            const label = formatDistributionEntryLabel(d);
            return (
              <SimpleMenuItem
                key={label}
                onAction={() =>
                  onChange([
                    ...value,
                    {
                      fieldId: field.fieldId,
                      fieldKey: field.fieldKey,
                      label,
                      filter:
                        "lower" in d
                          ? {
                              type: "number",
                              op: "range",
                              fieldId: field.fieldId,
                              lower: d.lower,
                              upper: d.upper,
                            }
                          : {
                              type: "enum",
                              op: "equals",
                              fieldId: field.fieldId,
                              value: d.value,
                            },
                    },
                  ])
                }
              >
                {label}
              </SimpleMenuItem>
            );
          })}
        </SimpleMenu>
      </SimpleMenuPopover>
    </SubmenuTrigger>
  );
}
